import {
  Drivers,
  ELD,
  DriverMessageIcon,
  AccountabilityIcon,
} from '@mgk-eld/assets';
import { SidebarItemsType } from '../../types/sidebar';
import BusinessCenterOutlined from '@mui/icons-material/BusinessCenterOutlined';
import DashboardOutlined from '@mui/icons-material/DashboardOutlined';
import DirectionsCarOutlined from '@mui/icons-material/DirectionsCarOutlined';
import EqualizerSharp from '@mui/icons-material/EqualizerSharp';
import FormatListBulletedOutlined from '@mui/icons-material/FormatListBulletedOutlined';
import PersonOutlineOutlined from '@mui/icons-material/PersonOutlineOutlined';
import {
  AppConfig,
  CompanyShort,
  Features,
  isNewFeature,
  WEBAPP_RELEASE,
} from '@mgk-eld/utils';
import PersonalVideoIcon from '@mui/icons-material/PersonalVideo';
import GroupsIcon from '@mui/icons-material/Groups';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import CameraAltOutlined from '@mui/icons-material/CameraAltOutlined';
import { AppUser, clientRoles } from '../../types/auth';
import { baseUrl } from '@mgk-eld/utils';
const ACCOUNT_LEVEL_ROLES = [
  clientRoles.accountAdmin,
  clientRoles.accounting,
  clientRoles.techSupport,
] as const;

const COMPANY_LEVEL_ROLES = [
  clientRoles.companyOwner,
  clientRoles.fleetManager,
] as const;

const isAccountLevelUser = (
  role: string | undefined
): role is (typeof ACCOUNT_LEVEL_ROLES)[number] => {
  return (
    role !== undefined &&
    ACCOUNT_LEVEL_ROLES.includes(role as (typeof ACCOUNT_LEVEL_ROLES)[number])
  );
};

const isCompanyLevelUser = (
  role: string | undefined
): role is (typeof COMPANY_LEVEL_ROLES)[number] => {
  return (
    role !== undefined &&
    COMPANY_LEVEL_ROLES.includes(role as (typeof COMPANY_LEVEL_ROLES)[number])
  );
};

const shouldShowDVR = (userInfo: AppUser | null): boolean => {
  if (!userInfo?.role_name) return false;

  return (
    (isAccountLevelUser(userInfo.role_name) ||
      isCompanyLevelUser(userInfo.role_name)) &&
    Boolean(userInfo.fleetlive_organization_id)
  );
};

const getPagesSection1 = (
  isDesktop: boolean,
  selectedCompany: CompanyShort | null,
  userInfo: AppUser | null,
  appConfig: AppConfig | null
) =>
  [
    {
      icon: DashboardOutlined,
      title: 'Dashboard',
      children: [
        {
          href: '/dashboard',
          title: 'Map',
        },
        { href: '/trackings', title: 'Trackings' },
      ],
    },
    {
      icon: FormatListBulletedOutlined,
      title: 'Logs',
      children: [
        {
          href: '/logs',
          title: 'Logs by day',
        },
        {
          href: '/bulk',
          title: 'Logs by period',
        },
        {
          href: '/totals',
          title: 'Worked hours',
        },
      ],
    },
    {
      href: '/reports',
      icon: EqualizerSharp,
      title: 'Reports',
    },

    {
      icon: PersonalVideoIcon,
      title: 'Safety',
      children: [
        {
          href: '/dots',
          title: 'DOT Inspections',
        },
        {
          href: '/monitoring',
          title: 'Monitoring',
        },
        {
          href: '/events',
          title: 'Events',
        },
        isDesktop && {
          href: '/errors',
          title: 'Errors',
        },
      ],
    },
    {
      href: '/wake-up',
      icon: DriverMessageIcon,
      title: 'Message Driver',
    },
    selectedCompany?.tracking_username && {
      badge: isNewFeature(Features.GPSTracking, WEBAPP_RELEASE),
      href: '/gps-tracking',
      icon: GpsFixedIcon,
      title: 'GPS Tracking',
    },
    shouldShowDVR(userInfo)
      ? {
          externalHref: baseUrl.getDVRAppUrl(appConfig?.domain),
          icon: CameraAltOutlined,
          title: 'AI Cameras',
          badge: isNewFeature(Features.DVR, WEBAPP_RELEASE),
        }
      : null,
  ] as unknown as SidebarItemsType[];

const getPagesSection2 = (isDesktop: boolean) =>
  [
    {
      href: '/users',
      icon: PersonOutlineOutlined,
      title: 'Users',
    },
    {
      href: '/drivers',
      icon: Drivers,
      title: 'Drivers',
    },
    {
      href: '/eld',
      icon: ELD,
      title: 'ELD',
    },
    {
      href: '/vehicles',
      icon: DirectionsCarOutlined,
      title: 'Vehicles',
    },
    isDesktop && {
      href: '/companies',
      icon: BusinessCenterOutlined,
      title: 'Companies',
    },
    {
      href: '/accountusers',
      icon: GroupsIcon,
      title: 'Account Users',
    },
    {
      badge: isNewFeature(Features.Accountability, WEBAPP_RELEASE),
      href: '/accountability',
      icon: AccountabilityIcon,
      title: 'Accountability',
    },
  ] as unknown as SidebarItemsType[];

const getPagesSection3 = (isDesktop: boolean, appConfig: AppConfig | null) => {
  return [
    !isDesktop && {
      href: '/user-profile',
      title: 'User Profile',
    },
    !isDesktop && {
      href: '/company-profile',
      title: 'Company Profile',
    },
    appConfig?.domain?.includes('mgk') && {
      externalHref: 'https://help.mgkeld.com/',
      title: 'Knowledge Base',
    },
    {
      title: 'Documentation',
      badge: isNewFeature(Features.Documentation, WEBAPP_RELEASE),
      href: '/documentation',
    },
    appConfig?.domain?.includes('mgk') &&
      isDesktop && {
        externalHref: 'https://help.mgkeld.com/contact',
        title: 'Feedback/Report',
      },
    !isDesktop && {
      href: '/logout',
      title: 'Logout',
    },
  ] as unknown as SidebarItemsType[];
};

const getNavItems = (
  isDesktop: boolean,
  appConfig: AppConfig | null,
  selectedCompany: CompanyShort | null,
  userInfo: AppUser | null
) => [
  {
    title: '',
    divider: true,
    pages: getPagesSection1(
      isDesktop,
      selectedCompany,
      userInfo,
      appConfig
    ).filter((i) => i),
  },
  {
    title: '',
    divider: true,
    pages: getPagesSection2(isDesktop).filter((i) => i),
  },
  {
    title: '',
    divider: false,
    pages: getPagesSection3(isDesktop, appConfig).filter((i) => i),
  },
];

export default getNavItems;
